import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { fmtDateTime } from "@/components/shared";

export function NotificationBell({ dark = false }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  const openItem = async (n) => {
    try { if (!n.read_at) await api.post(`/notifications/${n.id}/read`); } catch {}
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${dark ? "text-slate-300 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"}`}>
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="font-heading font-bold text-navy">Notifications</span>
          {unread > 0 && <button onClick={markAll} className="text-xs text-cyan-dark hover:underline" data-testid="notifications-markall">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Nothing new right now</div>
          ) : items.map((n) => {
            const clickable = !!n.link;
            return (
              <div
                key={n.id}
                {...(clickable ? { onClick: () => openItem(n), role: "button", tabIndex: 0 } : {})}
                data-testid={`notification-item-${n.id}`}
                className={`border-b border-slate-50 px-4 py-3 ${n.read_at ? "opacity-60" : "bg-cyan/5"} ${clickable ? "cursor-pointer transition-colors hover:bg-cyan/10" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read_at && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan" />}
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-navy">{n.title}</div>
                    <div className="text-xs text-slate-500">{n.message}</div>
                    <div className="mt-1 text-[10px] text-slate-400">{fmtDateTime(n.created_at)}</div>
                  </div>
                  {clickable && <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-cyan-dark" />}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
